import { useAppDispatch } from "@/store";
import { BRAND_NAME } from "@/utils/constants";

import endpoints from "@/api/endpoints";
import httpClient from "@/api/http-client";
import { selectAuth, authActions } from "@/store/slices/auth";
import { selectCart, fetchCartItems } from "@/store/slices/cart";
import product, { selectProduct } from "@/store/slices/product";
import Link from "next/link";
import { useRouter } from "next/router";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import styles from "./purchase.module.scss";
import { fbqTrackEvent } from "@/lib/fpixel";
import { ptrackEvent } from "@/lib/ppixel";
import { GA4_ITEM_BRAND } from "@/lib/ga4";
import { syteTrackEvent } from "@/lib/syte";
import { unbxdCusEngTrackEvent } from "@/lib/unbxd";

const ProductDetailsPurchase = () => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const [cartActionLoading, setCartActionLoading] = useState(false);
  const cartState = useSelector(selectCart);
  const authState = useSelector(selectAuth);
  const productState = useSelector(selectProduct);
  const [actionData, setActionData] = useState({ type: "", id: "" });
  const actionInProgress = useRef(false);

  const cartItem = useMemo(
    () =>
      cartState.items.find(
        (v) => v.identifier == productState.selectedPrice?.id?.toString() || ""
      ) || null,
    [cartState.items, productState.selectedPrice?.id]
  );

  const addToCart = async (redirect = false) => {
    if (!authState.user) {
      dispatch(authActions.setIsModalOpen(true));
      setActionData({
        type: "cart",
        id: `${productState?.selectedPrice?.id || ""}`,
      });
      return;
    }
    setCartActionLoading(true);

    try {
      await httpClient.post(endpoints.cart.add(), {
        is_solitaire: false,
        type: "product",
        identifier: productState.selectedPrice?.id,
      });
      if (window.Moengage) {
        window.Moengage.track_event("Added to Cart", {
          name: productState.data?.title,
          id: productState.data?.sku,
          amount: productState.selectedPrice?.discount
            ? productState.selectedPrice?.discount.total
            : productState.selectedPrice?.total,
        });
      }

      await dispatch(fetchCartItems());
      toast("Item added in cart", {
        type: "success",
        toastId: "cart-add-success",
      });
      fbqTrackEvent("AddToCart", {
        content_type: "product",
        content_ids: [productState.data?.sku],
      });
      ptrackEvent("addtocart", {
        event_id: `atc_${Date.now()}`,
        value: productState.selectedPrice?.discount
          ? productState.selectedPrice?.discount.total
          : productState.selectedPrice?.total,
        order_quantity: 1,
        currency: "INR",
      });
      if (
        productState.data?.categories &&
        productState.data?.categories.length
      ) {
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
          event: "add_to_cart",
          ecommerce: {
            currency: "INR",
            value: productState.selectedPrice?.discount
              ? productState.selectedPrice?.discount.total
              : productState.selectedPrice?.total,
            items: [
              {
                item_id: productState.data?.sku,
                item_name: productState.data?.title,
                item_brand: GA4_ITEM_BRAND,
                item_category: [...productState.data?.categories].reverse()[0]
                  .name,
                item_variant: productState.selectedPrice?.purity,
                price: productState.selectedPrice?.discount
                  ? productState.selectedPrice?.discount.total
                  : productState.selectedPrice?.total,
                quantity: 1,
              },
            ],
          },
        });
      }
      syteTrackEvent({
        name: "products_added_to_cart",
        tag: "ecommerce",
        osh_id: authState.user?.uuid,
        products: [
          {
            sku: productState.data?.sku,
            price: productState.selectedPrice?.discount
              ? productState.selectedPrice?.discount.total
              : productState.selectedPrice?.total,
            quantity: 1,
          },
        ],
      });
    } catch (error: any) {
      if (Number(error?.response?.status) === 400) {
        toast("Something went wrong! Refreshing the page!", {
          type: "error",
          toastId: "refresh-error",
        });
        window.location.reload();
      }
    }
      unbxdCusEngTrackEvent("Add to cart ", {
        source: "web",
        platform: "senco",
        prid: productState.data?.id,
        variant_id: productState.selectedPrice?.id,
        image_url: productState.data?.images[0].media,
        prqt: 1,
        product_name: productState.data?.title,
        category: productState.data?.categories?.length
          ? [...productState.data.categories].reverse()[0]?.name
          : "",
        brand: BRAND_NAME,
        amount: productState.selectedPrice?.total,
        product_url: `${window.location.origin}/jewellery/${productState.data?.slug}`,
        discounted_price: productState.selectedPrice?.discount?.price || 0,
        stock_availability: productState.data?.stock ? 1 : 0,
      });

    setCartActionLoading(false);
    if (redirect) router.push("/cart");
  };

  const removeFromCart = async () => {
    if (!confirm("Are you sure?")) return;
    setCartActionLoading(true);
    await httpClient.delete(endpoints.cart.remove(cartItem?.id));
    await dispatch(fetchCartItems());
    if (window.Moengage) {
      window.Moengage.track_event("Removed From Cart", {
        name: cartItem?.name,
        id: cartItem?.slug,
        amount: cartItem?.price,
      });
    }
      unbxdCusEngTrackEvent("Remove from cart ", {
        source: "web",
        platform: "senco",
        prid: productState.data?.id,
        variant_id: productState.selectedPrice?.id,
        image: productState.data?.images[0].media,
        prqt: productState.data?.stock || 1,
        product_name: productState.data?.title,
        category: productState.data?.categories?.length
          ? [...productState.data.categories].reverse()[0]?.name
          : "",
        brand: BRAND_NAME,
        amount: productState.selectedPrice?.total,
        product_url: `${window.location.origin}/jewellery/${productState.data?.slug}`,
      });
    toast("Item removed from cart", {
      type: "info",
      toastId: "cart-remove-success",
    });
    if (productState.data?.categories && productState.data?.categories.length) {
      window.gtag("event", "remove_from_cart", {
        currency: "INR",
        value: productState.selectedPrice?.discount
          ? productState.selectedPrice?.discount.total
          : productState.selectedPrice?.total,
        items: [
          {
            item_id: productState.data?.sku,
            item_name: productState.data?.title,
            item_category: [...productState.data?.categories].reverse()[0].name,
            price: productState.selectedPrice?.discount
              ? productState.selectedPrice?.discount.total
              : productState.selectedPrice?.total,
            quantity: 1,
          },
        ],
      });
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: "remove_from_cart",
        ecommerce: {
          currency: "INR",
          value: productState.selectedPrice?.discount
            ? productState.selectedPrice?.discount.total
            : productState.selectedPrice?.total,
          items: [
            {
              item_id: productState.data?.sku,
              item_name: productState.data?.title,
              item_category: [...productState.data?.categories].reverse()[0]
                .name,
              price: productState.selectedPrice?.discount
                ? productState.selectedPrice?.discount.total
                : productState.selectedPrice?.total,
              quantity: 1,
            },
          ],
        },
      });
    }
    setCartActionLoading(false);
  };

  useEffect(() => {
    const handleAction = async () => {
      if (actionInProgress.current) return;

      if (actionData.id && authState.user) {
        actionInProgress.current = true;

        try {
          if (actionData.type === "cart") {
            await addToCart();
          }
        } catch (error) {
          console.error("Error handling action:", error);
        } finally {
          setActionData({ type: "", id: "" });
          actionInProgress.current = false;
        }
      }
    };
    if (actionData.id && authState.user) {
      handleAction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState, actionData]);

  if (cartItem) {
    return (
      <div className={styles.purchase_info}>
        <button
          aria-label="Remove From Cart"
          type="button"
          role="button"
          className={styles.btn_2}
          onClick={removeFromCart}
          disabled={cartActionLoading}
        >
          Remove From Cart
          {cartActionLoading && (
            <Spinner animation="border" size="sm" className="ms-2" />
          )}
        </button>
        <Link href="/cart" passHref>
          <button
            aria-label="Buy Now"
            role="button"
            type="button"
            className={styles.btn_1}
            disabled={cartActionLoading}
          >
            Buy Now
            {/* {cartActionLoading && (
              <Spinner animation="border" size="sm" className="ms-2" />
            )} */}
          </button>
        </Link>
      </div>
    );
  }

  return (
    <div className={styles.purchase_info}>
      <button
        aria-label="Add to Cart"
        type="button"
        role="button"
        className={styles.btn_2}
        onClick={() => addToCart(false)}
        disabled={cartActionLoading}
      >
        Add to Cart
        {cartActionLoading && (
          <Spinner animation="border" size="sm" className="ms-2" />
        )}
      </button>
      <button
        aria-label="Buy Now"
        type="button"
        role="button"
        className={styles.btn_1}
        onClick={() => addToCart(true)}
        disabled={cartActionLoading}
      >
        Buy Now
        {/* {cartActionLoading && (
          <Spinner animation="border" size="sm" className="ms-2" />
        )} */}
      </button>
    </div>
  );
};

export default ProductDetailsPurchase;
